import { useEffect, useState } from 'react';
import { History } from 'lucide-react';
import sprayService from '../../services/spray';
import { formatDateTimeInTz, useOrgTimezone } from '@/utils/timezone';
import type { ChemicalHistoryEvent } from '../../types/spray';

/**
 * Who changed this chemical and its registrations, newest first.
 *
 * The organisation's own record (HHNodeServer `spray_registry_events`): each
 * organisation owns its chemicals, so nobody outside it sees who made or
 * changed one. A withholding period someone lowered is on record here.
 */

const ACTION_LABELS: Record<string, string> = {
  chemical_created: 'Added the chemical',
  chemical_updated: 'Changed the chemical',
  chemical_deactivated: 'Took the chemical out of use',
  registration_added: 'Added a registration',
  registration_updated: 'Changed a registration',
  bulk_rate: 'Set one rate across registrations',
  apvma_import: 'Imported registrations from APVMA',
  lookup_applied: 'Applied the APVMA lookup',
  label_applied: 'Applied rates read from the label',
  label_confirmed: 'Confirmed label rates',
};

const FIELD_LABELS: Record<string, string> = {
  whp_days: 'WHP harvest (days)',
  whp_export_days: 'WHP grazing (days)',
  rei_hours: 'REI (hours)',
  min_rate_per_ha: 'Min rate',
  max_rate_per_ha: 'Max rate',
  rate_unit: 'Rate unit',
  is_active: 'Active',
  situation_id: 'Situation',
  target_pest: 'Target pest',
  registration_number: 'Registration number',
  max_applications_per_season: 'Max applications a season',
  min_days_between_apps: 'Days between applications',
  growth_stage_restriction: 'Growth stage',
  rate_status: 'Rate status',
  product_name: 'Product name',
  apvma_approval_number: 'APVMA number',
  signal_word: 'Signal word',
  withholding_text: 'Withholding text',
  mode_of_action_group: 'Mode of action group',
  disease_activity: 'Disease activity',
  washoff_rain_mm: 'Wash-off rain (mm)',
  kickback_hours: 'Kickback (hours)',
  rates: 'Rate options',
  hostcodes: 'APVMA crops',
  registration_ids: 'Registrations',
  registrations_added: 'Registrations added',
  rows: 'Label rows',
};

function show(v: unknown): string {
  if (v == null || v === '') return '—';
  if (Array.isArray(v)) return `${v.length}`;
  if (typeof v === 'boolean') return v ? 'yes' : 'no';
  if (typeof v === 'object') return '…';
  return String(v);
}

function changeLines(e: ChemicalHistoryEvent): string[] {
  return Object.entries(e.changes || {}).map(([field, c]) => {
    const label = FIELD_LABELS[field] || field;
    return c.from == null ? `${label}: ${show(c.to)}` : `${label}: ${show(c.from)} → ${show(c.to)}`;
  });
}

interface Props {
  chemicalId: number;
  /** Bump to reload after a change on the page. */
  reloadKey?: number;
}

export default function ChemicalHistoryCard({ chemicalId, reloadKey = 0 }: Props) {
  const tz = useOrgTimezone();
  const [events, setEvents] = useState<ChemicalHistoryEvent[] | null>(null);

  useEffect(() => {
    let live = true;
    sprayService.getChemicalHistory(chemicalId).then((res) => {
      if (live) setEvents(res.success && res.data ? res.data : []);
    });
    return () => { live = false; };
  }, [chemicalId, reloadKey]);

  if (!events || !events.length) return null;

  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-5">
      <h3 className="text-sm font-semibold text-neutral-600 uppercase tracking-wide mb-3 flex items-center gap-2">
        <History className="h-4 w-4" />
        History
      </h3>
      <ul className="space-y-3">
        {events.map((e) => (
          <li key={e.id} className="text-sm border-l-2 border-neutral-200 pl-3 min-w-0">
            <p className="text-neutral-800 break-words">
              <span className="font-medium">{ACTION_LABELS[e.action] || e.action}</span>
              {(e.situation_name || e.target_pest) && (
                <span className="text-neutral-500"> · {[e.situation_name, e.target_pest].filter(Boolean).join(' / ')}</span>
              )}
            </p>
            {changeLines(e).length > 0 && (
              <ul className="text-xs text-neutral-600 mt-0.5 space-y-0.5">
                {changeLines(e).map((line) => <li key={line} className="break-words">{line}</li>)}
              </ul>
            )}
            <p className="text-xs text-neutral-500 mt-0.5">
              {e.user_name || 'Someone no longer here'} · {formatDateTimeInTz(e.created_at, tz)}
            </p>
          </li>
        ))}
      </ul>
    </div>
  );
}
